import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  TextField,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CustomerFormData, COMPANY_NAME_TYPE_OPTIONS } from '../../schemas/customerFormSchema';
import { FieldOptionDropdown } from '../FieldOptionDropdown';
import { InputAddress } from '../common/InputAddress';
import { TriStateCheckbox } from '../common/TriStateCheckbox';
import { ItalianNumberField } from '../common/ItalianNumberField';

export const CustomerDetailMainTab: React.FC = () => {
  const {
    register,
    watch,
    control,
    formState: { errors },
  } = useFormContext<CustomerFormData>();

  const isVoluntary = watch('voluntary');
  const isNonVoluntaryCompany = isVoluntary === false;

  return (
    <Box>
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={600}>
            Dati principali
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <TextField
                {...register('company_name')}
                sx={{ flex: 1, minWidth: 200 }}
                label="Ragione sociale"
                required
                error={!!errors.company_name}
                helperText={errors.company_name?.message}
                variant="outlined"
              />
              <Controller
                name="company_name_type"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    freeSolo
                    options={COMPANY_NAME_TYPE_OPTIONS}
                    value={field.value ?? ''}
                    inputValue={field.value ?? ''}
                    onInputChange={(_e, value) => field.onChange(value)}
                    onChange={(_e, newValue) => field.onChange(newValue ?? '')}
                    onBlur={field.onBlur}
                    sx={{ width: 220 }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Forma societaria"
                        placeholder="es. S.r.l., S.p.A."
                        error={!!errors.company_name_type}
                        helperText={errors.company_name_type?.message}
                        variant="outlined"
                      />
                    )}
                  />
                )}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <TextField
                  {...register('ateco_2025')}
                  fullWidth
                  label="ATECO 2025"
                  error={!!errors.ateco_2025}
                  helperText={errors.ateco_2025?.message}
                  variant="outlined"
                />
              </Box>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={600}>
            Dati impresa
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 2,
            }}
          >
            <Box>
              <Controller
                name="voluntary"
                control={control}
                render={({ field }) => (
                  <TriStateCheckbox
                    label="Impresa volontaria"
                    value={field.value ?? null}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                    error={!!errors.voluntary}
                    helperText={errors.voluntary?.message as string}
                    required={true}
                  />
                )}
              />
            </Box>

            <Box>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <FieldOptionDropdown
                    {...field}
                    tableCode="TIPO_IMPRESA"
                    label="Tipo di impresa (FieldOption: TIPO_IMPRESA)"
                    required={isNonVoluntaryCompany}
                    error={!!errors.type}
                    helperText={errors.type?.message as string}
                    variant="outlined"
                    isNullable
                  />
                )}
              />
            </Box>

            <Box>
              <Controller
                name="multi_site"
                control={control}
                render={({ field }) => (
                  <FormControl
                    fullWidth
                    required={isNonVoluntaryCompany}
                    error={!!errors.multi_site}
                  >
                    <InputLabel id="multi-site-label">Impresa monosito / multisito</InputLabel>
                    <Select
                      {...field}
                      labelId="multi-site-label"
                      label="Impresa monosito / multisito"
                      value={
                        field.value === undefined || field.value === null
                          ? ''
                          : field.value
                            ? 'multisito'
                            : 'monosito'
                      }
                      onChange={e => {
                        const v = e.target.value as string;
                        if (v === '') {
                          field.onChange(null);
                        } else {
                          field.onChange(v === 'multisito');
                        }
                      }}
                    >
                      <MenuItem value="">
                        <em>Non indicato</em>
                      </MenuItem>
                      <MenuItem value="monosito">Monosito</MenuItem>
                      <MenuItem value="multisito">Multisito</MenuItem>
                    </Select>
                    {errors.multi_site && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {errors.multi_site.message as string}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Box>

            <Box>
              <Controller
                name="clustering"
                control={control}
                render={({ field }) => (
                  <FieldOptionDropdown
                    {...field}
                    tableCode="CLUSTERIZZAZIONE"
                    label="Clusterizzazione (FieldOption: CLUSTERIZZAZIONE)"
                    required={isNonVoluntaryCompany}
                    error={!!errors.clustering}
                    helperText={errors.clustering?.message as string}
                    variant="outlined"
                    isNullable
                  />
                )}
              />
            </Box>

            <Box>
              <TextField
                {...register('employee_count', { valueAsNumber: true })}
                fullWidth
                label="Numero dipendenti (ULA)"
                type="number"
                error={!!errors.employee_count}
                helperText={errors.employee_count?.message as string}
                variant="outlined"
              />
            </Box>

            <Box>
              <Controller
                name="revenue"
                control={control}
                render={({ field }) => (
                  <ItalianNumberField
                    value={field.value as number | null | undefined}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    fullWidth
                    label="Fatturato (ultimo bilancio presentato) [€]"
                    error={!!errors.revenue}
                    helperText={errors.revenue?.message as string}
                    variant="outlined"
                  />
                )}
              />
            </Box>

            <Box>
              <Controller
                name="balance_sheet_assets"
                control={control}
                render={({ field }) => (
                  <ItalianNumberField
                    value={field.value as number | null | undefined}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    fullWidth
                    label="Attivo patrimoniale (ultimo bilancio presentato) [€]"
                    error={!!errors.balance_sheet_assets}
                    helperText={errors.balance_sheet_assets?.message as string}
                    variant="outlined"
                  />
                )}
              />
            </Box>

            <Box>
              <Controller
                name="size_category"
                control={control}
                render={({ field }) => (
                  <FieldOptionDropdown
                    {...field}
                    tableCode="CATEGORIA_DIMENSIONALE"
                    label="Categoria dimensionale (FieldOption: CATEGORIA_DIMENSIONALE)"
                    required={isNonVoluntaryCompany}
                    error={!!errors.size_category}
                    helperText={errors.size_category?.message as string}
                    variant="outlined"
                    isNullable
                  />
                )}
              />
            </Box>

            <Box>
              <Controller
                name="consumption_category"
                control={control}
                render={({ field }) => (
                  <FieldOptionDropdown
                    {...field}
                    tableCode="CATEGORIA_DI_CONSUMO"
                    label="Categoria di consumo (FieldOption: CATEGORIA_DI_CONSUMO)"
                    error={!!errors.consumption_category}
                    helperText={errors.consumption_category?.message as string}
                    variant="outlined"
                    isNullable
                  />
                )}
              />
            </Box>

            <Box>
              <TextField
                {...register('average_annual_consumption', { valueAsNumber: true })}
                fullWidth
                label="Consumo annuo medio di energia (ultimi 3 anni) [TJ]"
                type="number"
                error={!!errors.average_annual_consumption}
                helperText={errors.average_annual_consumption?.message as string}
                variant="outlined"
              />
            </Box>

            <Box>
              <Controller
                name="high_electricity_consumption"
                control={control}
                render={({ field }) => (
                  <TriStateCheckbox
                    label="Impresa a forte consumo di energia elettrica"
                    value={field.value ?? null}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                    required={isNonVoluntaryCompany}
                    error={!!errors.high_electricity_consumption}
                    helperText={errors.high_electricity_consumption?.message as string}
                  />
                )}
              />
            </Box>

            <Box>
              <Controller
                name="high_gas_consumption"
                control={control}
                render={({ field }) => (
                  <TriStateCheckbox
                    label="Impresa a forte consumo di gas"
                    value={field.value ?? null}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                    required={isNonVoluntaryCompany}
                    error={!!errors.high_gas_consumption}
                    helperText={errors.high_gas_consumption?.message as string}
                  />
                )}
              />
            </Box>

            <Box>
              <Controller
                name="certifications"
                control={control}
                render={({ field }) => {
                  const valueStr = field.value ?? '';
                  const valueArray = valueStr
                    ? valueStr
                        .split(',')
                        .map(s => s.trim())
                        .filter(Boolean)
                    : [];
                  return (
                    <Autocomplete
                      multiple
                      freeSolo
                      options={['9001', '14001', '45001', '50001', 'EMAS']}
                      value={valueArray}
                      onChange={(_e, newValue: string[]) => {
                        field.onChange(newValue.join(','));
                      }}
                      onBlur={field.onBlur}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Certificazioni aziendali"
                          error={!!errors.certifications}
                          helperText={errors.certifications?.message as string}
                          variant="outlined"
                        />
                      )}
                    />
                  );
                }}
              />
            </Box>

            <Box>
              <TextField
                {...register('vat_number')}
                fullWidth
                label="P. IVA"
                required={isNonVoluntaryCompany}
                error={!!errors.vat_number}
                helperText={errors.vat_number?.message}
                variant="outlined"
              />
            </Box>

            <Box>
              <TextField
                {...register('tax_code')}
                fullWidth
                label="Codice Fiscale"
                required={isNonVoluntaryCompany}
                error={!!errors.tax_code}
                helperText={errors.tax_code?.message}
                variant="outlined"
              />
            </Box>

            <Box>
              <InputAddress
                name="address"
                label="Indirizzo sede legale"
                required={isNonVoluntaryCompany}
              />
            </Box>

            <Box>
              <TextField
                {...register('certified_email')}
                fullWidth
                label="PEC aziendale"
                required={isNonVoluntaryCompany}
                error={!!errors.certified_email}
                helperText={errors.certified_email?.message}
                variant="outlined"
              />
            </Box>

            <Box>
              <TextField
                {...register('legal_representative')}
                fullWidth
                label="Legale rappresentante"
                required={isNonVoluntaryCompany}
                error={!!errors.legal_representative}
                helperText={errors.legal_representative?.message}
                variant="outlined"
              />
            </Box>

            <Box>
              <TextField
                {...register('legal_representative_phone')}
                fullWidth
                label="Telefono legale rappresentante"
                error={!!errors.legal_representative_phone}
                helperText={errors.legal_representative_phone?.message}
                variant="outlined"
              />
            </Box>

            <Box>
              <TextField
                {...register('legal_representative_email')}
                fullWidth
                label="E-mail legale rappresentante"
                error={!!errors.legal_representative_email}
                helperText={errors.legal_representative_email?.message}
                variant="outlined"
              />
            </Box>

            <Box>
              <TextField
                {...register('contact_person')}
                fullWidth
                label="Referente aziendale diagnosi"
                required={isNonVoluntaryCompany}
                error={!!errors.contact_person}
                helperText={errors.contact_person?.message}
                variant="outlined"
              />
            </Box>

            <Box>
              <TextField
                {...register('contact_person_phone')}
                fullWidth
                label="Telefono referente aziendale"
                error={!!errors.contact_person_phone}
                helperText={errors.contact_person_phone?.message}
                variant="outlined"
              />
            </Box>

            <Box>
              <TextField
                {...register('contact_person_email')}
                fullWidth
                label="E-mail referente aziendale"
                error={!!errors.contact_person_email}
                helperText={errors.contact_person_email?.message}
                variant="outlined"
              />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
